// User-App-facing selfie-completion API service.
//
// Talks to POST /selfies (separate from Admin's /admin/selfies CRUD) so the
// authenticated Dealzta user's captured/picked photo is stored server-side
// and their mission is advanced past the selfie step. Requests go through
// apiClient so the Dealzta bearer token is attached — user_id is never
// sent; the backend derives the acting user from the token alone.

import { apiFetch } from './apiClient';

/**
 * Converts a data: URL (as produced by camera.js/crop.js canvases) into a
 * Blob suitable for a multipart file upload.
 */
function dataUrlToBlob(dataUrl) {
  const [meta, base64] = dataUrl.split(',');
  const mimeMatch = /data:(.*);base64/.exec(meta || '');
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';

  const binary = atob(base64 || '');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], { type: mime });
}

/**
 * Complete the selfie mission step (POST /selfies, multipart/form-data).
 * Sends only user_mission_id, the photo file, and its source — never
 * user_id, selfie_completed_at, or current_step, which the backend alone
 * derives/decides.
 *
 * Resolves to { selfie, user_mission }. Throws on a non-2xx/non-success
 * response (including mission-not-owned/not-found and skip-ahead
 * rejections) so the caller can decide how to handle the failure.
 */
export async function uploadSelfie(userMissionId, photoDataUrl, source) {
  const formData = new FormData();
  formData.append('user_mission_id', String(userMissionId));
  formData.append('source', source);
  formData.append('selfie', dataUrlToBlob(photoDataUrl), 'selfie.jpg');

  const response = await apiFetch('/selfies', {
    method: 'POST',
    body: formData,
  });

  const body = await response.json();

  if (!response.ok || !body.success) {
    throw new Error(body.message || 'Unable to upload selfie.');
  }

  return body.data;
}
